// M8 已切真后端（/api/articles/{id} + /api/comments?article_id=…）
// 单篇分析：5 大指标 + 完读率漏斗 + 来源横向条 + 24h 分布 + 读者画像 + 留言
// 对齐 WeChat MP 「单篇分析」:5 KPI(阅读/分享/留言/在看/完读率) + 来源改 SourceBar
// + 「距群发已 X 天,数据持续更新中」提示
import { useParams, useNavigate, Link } from 'react-router-dom'
import { Card, Tag, Alert, Avatar, Divider } from 'antd'
import { ArrowLeft, ExternalLink, Info } from 'lucide-react'
import ReactECharts from 'echarts-for-react'
import PageHeader from '@/components/PageHeader'
import KPICard from '@/components/KPICard'
import FunnelChart from '@/components/FunnelChart'
import PieChart from '@/components/PieChart'
import SourceBar from '@/components/SourceBar'
import { wechatTheme } from '@/lib/theme'
import { useArticleDetail } from '@/mocks/articles'
import { useCommentList } from '@/mocks/comments'
import { formatNumber, formatPercent, timeAgo } from '@/lib/utils'

export default function ArticleDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { data, isLoading } = useArticleDetail(id)
  const articleId = data?.id
  const { data: comments } = useCommentList({ articleId })

  if (isLoading || !data) {
    return (
      <div className="text-sm text-ink-300 py-12 text-center">加载中…</div>
    )
  }

  const hourOption = {
    tooltip: { trigger: 'axis' as const, backgroundColor: wechatTheme.bg.card, borderColor: wechatTheme.border.DEFAULT, textStyle: { color: wechatTheme.text.primary, fontSize: 12 } },
    grid: { left: 40, right: 16, top: 16, bottom: 28 },
    xAxis: {
      type: 'category' as const,
      data: data.readHourDist.map((d) => `${d.hour}h`),
      axisLine: { lineStyle: { color: wechatTheme.border.DEFAULT } },
      axisLabel: { color: wechatTheme.text.tertiary, fontSize: 10 },
      axisTick: { show: false },
    },
    yAxis: {
      type: 'value' as const,
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: { lineStyle: { color: wechatTheme.border.DEFAULT, type: 'dashed' as const } },
      axisLabel: { color: wechatTheme.text.tertiary, fontSize: 10, formatter: (v: number) => formatNumber(v) },
    },
    series: [
      {
        type: 'bar',
        data: data.readHourDist.map((d) => d.count),
        itemStyle: { color: wechatTheme.brand.DEFAULT, borderRadius: [2, 2, 0, 0] },
        barWidth: 10,
      },
    ],
  }

  // 来源数据 → 横向条
  const sourceBarData = data.sourceDist
    .slice()
    .sort((a, b) => b.value - a.value)
    .map((s) => ({ name: s.name, value: s.value }))

  return (
    <div>
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-1 text-sm text-ink-500 hover:text-ink-900 mb-3 transition-colors"
      >
        <ArrowLeft size={14} /> 返回
      </button>

      <PageHeader
        title={data.title}
        subtitle={`${data.author || '—'} · 发布于 ${new Date(data.publishTime).toLocaleString('zh-CN')}`}
        extra={
          <Link
            to="#"
            onClick={(e) => e.preventDefault()}
            className="text-sm text-link hover:text-link-hover flex items-center gap-1 transition-colors"
          >
            原文 <ExternalLink size={14} />
          </Link>
        }
      />

      {/* 数据更新提示(代替「过期」告警 — WeChat MP 用「距群发 X 天,数据持续更新」) */}
      <Alert
        showIcon
        icon={<Info size={16} className="text-primary-600" />}
        type="info"
        className="!mb-4"
        message={`距群发已 ${data.daysSincePublished} 天,数据持续更新中${
          data.funnelExpireInDays > 0
            ? ` · 完读率漏斗 ${data.funnelExpireInDays} 天后从官方消失`
            : ''
        }`}
      />

      {/* 5 大指标 — WeChat 口径:阅读 / 分享 / 留言 / 在看 / 完读率 */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
        <KPICard label="阅读" value={data.readUser} unit="人" className="card-interactive" />
        <KPICard label="分享" value={data.shareCount} unit="次" className="card-interactive" />
        <KPICard label="留言" value={data.commentCount} unit="条" className="card-interactive" />
        <KPICard label="在看" value={data.lookingCount} unit="个" className="card-interactive" />
        <KPICard
          label="完读率"
          value={data.readFinishRate > 0 ? data.readFinishRate * 100 : null}
          unit="%"
          className="card-interactive"
        />
      </div>

      {/* 漏斗 + 来源 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <Card title="阅读完成率漏斗" size="small" className="card-interactive">
          <FunnelChart data={data.funnel} />
        </Card>
        <Card title="阅读来源占比" size="small" className="card-interactive">
          <SourceBar data={sourceBarData} maxItems={8} />
        </Card>
      </div>

      {/* 24h + 画像 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <Card title="24h 时段分布" size="small" className="card-interactive">
          <ReactECharts option={hourOption} style={{ height: 240, width: '100%' }} opts={{ renderer: 'canvas' }} />
        </Card>
        <Card title="读者画像 (7 天)" size="small" className="card-interactive">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <h4 className="text-sm text-ink-500 mb-2">性别</h4>
              <PieChart data={data.readerAttr.gender} height={180} />
            </div>
            <div>
              <h4 className="text-sm text-ink-500 mb-2">终端</h4>
              <PieChart data={data.readerAttr.device} height={180} />
            </div>
            <div>
              <h4 className="text-sm text-ink-500 mb-2">地域 TOP</h4>
              <ul className="text-sm space-y-1.5">
                {data.readerAttr.region.slice(0, 6).map((r) => (
                  <li key={r.name} className="flex justify-between text-ink-700 hover:bg-ink-50 px-2 py-1 rounded transition-colors">
                    <span>{r.name}</span>
                    <span className="num text-ink-500">{r.value}%</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Card>
      </div>

      {/* 留言 */}
      <Card
        title={`留言列表 (${comments?.length ?? 0})`}
        size="small"
        extra={
          <span className="text-xs text-ink-500">完读率 {formatPercent(data.readFinishRate)}</span>
        }
        className="card-interactive"
      >
        <ul className="flex flex-col divide-y divide-ink-100">
          {(comments ?? []).slice(0, 15).map((c) => (
            <li key={c.id} className="py-3 flex gap-3 group hover:bg-ink-50 transition-colors rounded">
              <Avatar size={28} className="!bg-primary-100 !text-primary-700 flex-shrink-0">
                {c.nickname?.[0] ?? '?'}
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-medium text-ink-900 truncate">{c.nickname ?? '(匿名)'}</span>
                  {c.isTop && <Tag color="gold">置顶</Tag>}
                  {c.status === 'elected' && <Tag color="success">精选</Tag>}
                  {c.status === 'pending' && <Tag color="warning">待回复</Tag>}
                  <span className="text-xs text-ink-300 ml-auto num">{timeAgo(c.createTime)}</span>
                </div>
                <p className="text-sm text-ink-700 leading-relaxed">{c.content}</p>
                {c.replyContent && (
                  <>
                    <Divider className="!my-2" />
                    <p className="text-sm text-primary-700 bg-primary-50 px-3 py-2 rounded">
                      <span className="text-xs text-primary-500 mr-1">作者回复：</span>
                      {c.replyContent}
                    </p>
                  </>
                )}
              </div>
            </li>
          ))}
        </ul>
      </Card>
    </div>
  )
}
